'use client';

import { AppBar, Box, ListItemButton, ListItemButtonProps, ListItemText, Stack } from "@mui/material";
import FlyoutInfo from "@/app/features/sites/header/ui/desktop/flyout-info/FlyoutInfo";
import React, { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import { SubmenuItem, SUBMENU_ITEMS } from "@/app/features/sites/header/types/menu-items";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import MainMenu from "@/app/features/sites/header/ui/desktop/MainMenu";
import { preloadAllFlyoutContent } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/data-access/preload";

export default function DesktopHeader({
    tickerBar
}: {
    tickerBar: ReactNode
}) {
    const INITIAL_MOUSE_OPEN_DELAY_MS = 240;
    const ACTIVE_VIEW_SWITCH_DELAY_MS = 70;

    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [flyoutHasFocus, setFlyoutHasFocus] = useState<boolean>(false);
    const [focusedMenuIndex, setFocusedMenuIndex] = useState<number | null>(null);
    const [activeMenuItem, setActiveMenuItem] = useState<SubmenuItem['id'] | null>(null);
    const [lastInteractionType, setLastInteractionType] = useState<'mouse' | 'keyboard'>('keyboard');
    const itemRefs = useRef<(any | null)[]>([]);
    const router = useRouter();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                setLastInteractionType('keyboard');
            }
        };

        const handleMouseDown = () => {
            setLastInteractionType('mouse');
        };

        window.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('mousedown', handleMouseDown, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('mousedown', handleMouseDown, true);
        };
    }, []);

    useEffect(() => {
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }

        if (focusedMenuIndex !== null) {
            if (lastInteractionType === 'mouse') {
                const nextMenuId = SUBMENU_ITEMS[focusedMenuIndex].id;
                if (nextMenuId === activeMenuItem) return;
                if (nextMenuId === 'home') return;

                const openDelay = activeMenuItem === null
                    ? INITIAL_MOUSE_OPEN_DELAY_MS
                    : ACTIVE_VIEW_SWITCH_DELAY_MS;

                openTimeoutRef.current = setTimeout(() => {
                    setActiveMenuItem(nextMenuId);
                }, openDelay);
            }
        }

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }

        // Closing logic: after 750ms without focus or mouse anywhere, close
        closeTimeoutRef.current = setTimeout(() => {
            if (!flyoutHasFocus && focusedMenuIndex == null) {
                setActiveMenuItem(null);
            }
        }, 750);

        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            if (openTimeoutRef.current) {
                clearTimeout(openTimeoutRef.current);
            }
        };
    }, [flyoutHasFocus, focusedMenuIndex, lastInteractionType, activeMenuItem]);

    const pathname = usePathname();

    useEffect(() => {
        resetMenuFocus()
    }, [pathname]);

    useEffect(() => {
        if (pathname !== '/') return;

        const preloadTimeoutId = window.setTimeout(() => {
            void preloadAllFlyoutContent();
        }, 250);

        return () => {
            window.clearTimeout(preloadTimeoutId);
        };
    }, [pathname]);

    function resetMenuFocus() {
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }

        setFocusedMenuIndex(null);
        setFlyoutHasFocus(false)
    }

    function handleKeyDown(e: React.KeyboardEvent, index: number) {
        setLastInteractionType('keyboard');
        let nextIndex: number | null = null;

        switch (e.key) {
            case 'ArrowRight':
                nextIndex = (index + 1) % SUBMENU_ITEMS.length;
                break;
            case 'ArrowLeft':
                nextIndex = (index - 1 + SUBMENU_ITEMS.length) % SUBMENU_ITEMS.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = SUBMENU_ITEMS.length - 1;
                break;
            case 'Enter':
            case ' ':
                const selectedItem = SUBMENU_ITEMS[index];

                if (selectedItem.route) {
                    e.preventDefault();
                    setActiveMenuItem(null);
                    router.push(selectedItem.route);
                    break;
                }

                e.preventDefault();
                setActiveMenuItem(selectedItem.id);
                // Move focus into flyout - we need to wait until it is rendered
                setTimeout(() => {
                    const flyout = document.querySelector('.flyout-container [autoFocus], .flyout-container button, .flyout-container a, .flyout-container input') as HTMLElement;
                    flyout?.focus();
                }, 100);
                break;
        }

        if (nextIndex !== null) {
            e.preventDefault();
            setFocusedMenuIndex(nextIndex);
            itemRefs.current[nextIndex]?.focus();
        }
    }

    function handleFlyoutKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Escape') {
            const indexToFocus = SUBMENU_ITEMS.findIndex(item => item.id === activeMenuItem);
            setActiveMenuItem(null);
            setFlyoutHasFocus(false);
            if (indexToFocus !== -1) {
                setFocusedMenuIndex(indexToFocus);
                // We use setTimeout to ensure the focus is applied after the state updates
                setTimeout(() => {
                    itemRefs.current[indexToFocus]?.focus();
                }, 0);
            }
        }
    }

    return (
        <Box
            onMouseLeave={resetMenuFocus}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    resetMenuFocus();
                }
            }}
        >
            <AppBar className="hidden md:flex flex-auto flex-col items-center px-8">
                {tickerBar}
                <MainMenu />
                <Stack
                    direction="row"
                    className="p-2"
                    onKeyDown={(e) => {
                        if (e.key === 'Tab') {
                            setLastInteractionType('keyboard');
                        }
                    }}
                >
                    {SUBMENU_ITEMS.map((item, index) => (
                        <MenuItemView
                            ref={(el) => { itemRefs.current[index] = el; }}
                            key={item.id}
                            item={item}
                            isFocused={focusedMenuIndex === index || (focusedMenuIndex === null && index === 0)}
                            isActive={activeMenuItem === item.id}
                            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index)}
                            onClick={(e: React.MouseEvent) => {
                                setLastInteractionType('keyboard');
                                if (item.route) {
                                    e.preventDefault();
                                    setActiveMenuItem(null);
                                    router.push(item.route);
                                    return;
                                }

                                setActiveMenuItem(item.id);
                            }}
                            onMouseEnter={() => {
                                setLastInteractionType('mouse');
                                setFocusedMenuIndex(index);
                            }}
                            onMouseLeave={() => {
                                setLastInteractionType('mouse');
                                setFocusedMenuIndex(null);
                            }}
                            onFocus={() => {
                                setFocusedMenuIndex(index);
                            }}
                            onBlur={() => {
                                setFocusedMenuIndex(null);
                            }}
                        />
                    ))}

                </Stack>
            </AppBar>
            <FlyoutInfo
                visibleFlyoutInfo={activeMenuItem}
                onFocus={() => setFlyoutHasFocus(true)}
                onEnter={() => {
                    setLastInteractionType('mouse');
                    setFlyoutHasFocus(true);
                }}
                onBlur={() => setFlyoutHasFocus(false)}
                onLeave={() => {
                    setLastInteractionType('mouse');
                    setFlyoutHasFocus(false);
                }}
                onKeyDown={handleFlyoutKeyDown}
            />
        </Box>
    )
}

const MenuItemView = forwardRef<
    any,
    ListItemButtonProps<any> & {
    item: SubmenuItem,
    isFocused: boolean,
    isActive: boolean,
}
>(({ item, isFocused, isActive, ...props }, ref) => {
    return (
        <ListItemButton
            {...(item.route
                ? { component: Link, href: item.route, prefetch: true }
                : {}
            )}
            ref={ref}
            {...props}
            aria-label={item.label || item.ariaLabel}
            tabIndex={isFocused ? 0 : -1} // Roving Tabindex
            sx={{
                py: 0,
                height: 'auto',
                display: 'flex',
                ...signatureBorderStyle,
            }}
        >
            {item.icon && (
                <span className="mr-4">{item.icon}</span>
            )}
            <ListItemText
                primary={item.label}
                color={isActive ? 'primary.main' : 'inherit'}
                slotProps={{
                    primary: {
                        style: {
                            transition: 'color .3s ease'
                        },
                        fontSize: 14,
                    }
                }}
            />
        </ListItemButton>
    );
});
MenuItemView.displayName = 'MenuItemView';
